import { colors } from "@zfp-akademie-a-s/zfp-design";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { generateCompoundInterestData } from "./CommonChartUtils";

type GenerateFinancialReserveChartDataParams = {
  readonly currentAmount: number;
  readonly monthlyContributionAmount: number;
  readonly accumulationPhaseDuration: number;
  readonly interestRate: number;
  readonly inflationRate: number;
};

type FinancialReserveChartData = {
  readonly currentReserveData: number[];
  readonly accumulationPhaseData: number[];
  readonly appreciationPhaseData: number[];
  readonly actualDurationInMonths: number;
  readonly milestoneDifferenceRatio: number;
};

export type CalculateMonthlyContributionParams = {
  readonly targetAmount: number;
  readonly currentAmount: number;
  readonly durationInMonths: number;
  readonly interestRate: number;
  readonly inflationRate: number;
};

export const generateFinancialReserveChartData = ({
  monthlyContributionAmount,
  accumulationPhaseDuration,
  interestRate,
  inflationRate,
  currentAmount,
}: GenerateFinancialReserveChartDataParams): FinancialReserveChartData => {
  const appreciationPhaseDuration = 120;
  const currentReserveData = new Array(accumulationPhaseDuration + 1).fill(currentAmount);
  const accumulationPhaseData = generateAccumulationPhaseData(
    currentAmount,
    monthlyContributionAmount,
    accumulationPhaseDuration,
    interestRate,
    inflationRate,
  );
  const accumulatedAmount = accumulationPhaseData[accumulationPhaseData.length - 1];
  const appreciationPhaseData = accumulationPhaseData.concat(
    generateCompoundInterestData({
      startAmount: accumulatedAmount,
      monthlyContributionAmount: 0,
      inflationRate,
      interestRate,
      durationInMonths: appreciationPhaseDuration,
    }),
  );

  const actualDurationInMonths = accumulationPhaseDuration + appreciationPhaseDuration;

  const milestoneDifferenceRatio = Math.abs(accumulationPhaseData.length - actualDurationInMonths);

  return {
    currentReserveData,
    accumulationPhaseData,
    appreciationPhaseData,
    milestoneDifferenceRatio,
    actualDurationInMonths,
  };
};

export const calculateMonthlyContribution = ({
  targetAmount,
  currentAmount,
  durationInMonths,
  interestRate = 0,
  inflationRate = 0,
}: CalculateMonthlyContributionParams): number => {
  const monthlyCompoundRate = (interestRate - inflationRate) / 100 / 12;

  if (Math.abs(monthlyCompoundRate) < 1e-10) {
    return (targetAmount - currentAmount) / durationInMonths;
  }

  const denominator = -1 + (1 + monthlyCompoundRate) ** durationInMonths;

  if (Math.abs(denominator) < 1e-10) {
    return (targetAmount - currentAmount) / durationInMonths;
  }

  const monthlyContribution =
    (targetAmount - currentAmount * (1 + monthlyCompoundRate) ** durationInMonths) * (monthlyCompoundRate / denominator);

  return monthlyContribution;
};

export const getFinancialReserveChartAnnotations = (targetAmount: number, currentAmount: number, accumulationPhaseDuration: number) => {
  const commonLineProps = {
    type: "line" as const,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderWidth: 1,
    borderDash: [8, 4],
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    font: {
      weight: 400,
      size: 10,
    },
  };

  return {
    reserveCreated: {
      yMin: 0,
      yMax: targetAmount,
      xMin: accumulationPhaseDuration,
      xMax: accumulationPhaseDuration,
      borderColor: "#000",
      borderWidth: 1,
      borderDash: [3, 5],
    },
    reserveCreatedDot: {
      type: "point" as const,
      xValue: accumulationPhaseDuration,
      yValue: 0,
      backgroundColor: "#000",
      borderWidth: 0,
      radius: 4,
    },
    currentAmount: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(currentAmount),
        color: colors.white,
      },
      ...commonLineProps,
      borderColor: colors.white,
      xMin: 0,
      xMax: accumulationPhaseDuration,
      yMax: currentAmount,
      yMin: currentAmount,
    },
    targetAmount: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmount),
      },
      ...commonLineProps,
      yMax: targetAmount,
      yMin: targetAmount,
    },
  };
};

const generateAccumulationPhaseData = (
  startAmount: number,
  monthlyContributionAmount: number,
  durationInMonths: number,
  interestRate: number,
  inflationRate: number,
) => {
  const result: number[] = [];
  const monthlyCompoundRate = (interestRate - inflationRate) / 100 / 12;

  for (let i = 0; i <= durationInMonths; i++) {
    let formula: number;

    if (Math.abs(monthlyCompoundRate) < 1e-10) {
      formula = startAmount + monthlyContributionAmount * i;
    } else {
      formula =
        startAmount * (1 + monthlyCompoundRate) ** i +
        (monthlyContributionAmount * (-1 + (1 + monthlyCompoundRate) ** i)) / monthlyCompoundRate;
    }

    result.push(formula);
  }

  return result;
};
